/**
 * Coach Dashboard — Full client management page for coach_pro tier.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TextInput,
  TouchableOpacity, ActivityIndicator, RefreshControl, Alert,
  Image, Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { resolveCoachOwnerOrg, reconcileCoachOrg } from '@/lib/orgProvisioning';
import { supabase } from '@/lib/supabase';
import { listMyClientLinks, resolveProfileById, type TrainerClientLink ,
  getMyTrainerProfile,
  createTrainerProfile,
  updateTrainerProfile,
  requestTrainerClientLinkByUsername,
  type TrainerProfile,
} from '@/lib/trainers';
import ClientCard, { type ClientCardData } from '@/components/coach/ClientCard';
import ClientInvite from '@/components/coach/ClientInvite';
import ProgramBuilder from '@/components/coach/ProgramBuilder';
import GroupMessage from '@/components/coach/GroupMessage';
import { loadCoachMessages, type CoachMessage } from '@/components/coach/GroupMessage';
import ClientComparison from '@/components/coach/ClientComparison';
import ProgressAlerts from '@/components/coach/ProgressAlerts';
import { loadPrograms, assignProgram, deleteProgram, type Program } from '@/lib/programBuilder';
import { getRevenueSnapshot, type RevenueSnapshot } from '@/lib/coachRevenue';
import RevenueDashboard from '@/components/coach/RevenueDashboard';
import {
  TagFilterBar, TagManager, AssignClientTags,
  loadTags, loadAssignments, type CoachTag, type ClientTagAssignment,
} from '@/components/coach/ClientTags';
import {
  ArrowLeft, Plus, Users, Search, ClipboardList, Trash2, UserPlus,
  MessageSquare, BarChart2, DollarSign, Calendar, Settings, Camera,
  Upload, Shield, Edit2, Tag, Download,
} from 'lucide-react-native';
import { exportCoachRoster } from '@/lib/dataExport';
import * as ImagePicker from 'expo-image-picker';
import { getTodaySessions, seedMockSessions, type CoachSession } from '@/lib/coachSchedule';
import { TYPO } from '@/constants/typography';
import { captureError } from '@/lib/errorReporting';
import TutorialOverlay from '@/components/TutorialOverlay';
import { useCoachAccess } from '@/lib/coachAccess';

interface DashboardStats {
  activeClients: number;
  workoutsThisWeek: number;
  avgAdherence: number;
}

export default function CoachDashboard() {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const { tier } = useSubscription();
  const router = useRouter();
  const c = theme.colors;

  // Hard route guard: coach_pro (or org_pro) required.
  // Replaces the previous soft "coming soon" gate with a redirect.
  const { hasAccess, isLoading: accessLoading } = useCoachAccess();
  useEffect(() => {
    if (!accessLoading && !hasAccess) {
      router.replace('/(tabs)/home');
    }
  }, [accessLoading, hasAccess]);

  // orgId can be passed as a route param (from gate.tsx startup routing or settings nav).
  // If not provided, we resolve it lazily from organization_memberships.
  const params = useLocalSearchParams<{ orgId?: string }>();
  const [coachOrgId, setCoachOrgId] = useState<string | null>(params.orgId ?? null);

  const [clients, setClients] = useState<ClientCardData[]>([]);
  const [stats, setStats] = useState<DashboardStats>({ activeClients: 0, workoutsThisWeek: 0, avgAdherence: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [activeTab, setActiveTab] = useState<'clients' | 'programs' | 'messages' | 'manage'>('clients');
  const [programs, setPrograms] = useState<Program[]>([]);
  const [showProgramBuilder, setShowProgramBuilder] = useState(false);
  const [editingProgram, setEditingProgram] = useState<Program | undefined>();
  const [showGroupMessage, setShowGroupMessage] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [messages, setMessages] = useState<CoachMessage[]>([]);
  const [revenue, setRevenue] = useState<RevenueSnapshot | null>(null);
  const [trainerProfile, setTrainerProfile] = useState<TrainerProfile | null>(null);
  const [clientUsername, setClientUsername] = useState('');
  const [bio, setBio] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [todaySessionCount, setTodaySessionCount] = useState(0);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editBio, setEditBio] = useState('');
  const [uploadingCert, setUploadingCert] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  // Gym members for client invite
  const [gymMembers, setGymMembers] = useState<{ id: string; display_name: string | null; handle: string | null; avatar_url: string | null }[]>([]);
  const [gymMemberSearch, setGymMemberSearch] = useState('');
  const [sortBy, setSortBy] = useState<'recent' | 'strength' | 'name'>('recent');
  const [prClientIds, setPrClientIds] = useState<Set<string>>(new Set());
  const [showRevenueDashboard, setShowRevenueDashboard] = useState(false);
  const [tags, setTags] = useState<CoachTag[]>([]);
  const [tagAssignments, setTagAssignments] = useState<ClientTagAssignment>({});
  const [activeTagFilter, setActiveTagFilter] = useState<string | null>(null);
  const [showTagManager, setShowTagManager] = useState(false);
  const [showAssignTags, setShowAssignTags] = useState<{ clientId: string; clientName: string } | null>(null);
  const isDev = __DEV__;
  const [dismissedNudge, setDismissedNudge] = useState(false);
  const [setupBannerDismissed, setSetupBannerDismissed] = useState(true); // default true until loaded

  // Resolve personal coaching org on mount (idempotent, safe).
  // Uses route param first; falls back to organization_memberships lookup.
  // This replaces the old profile.gym_id bug (gym_id is from gyms table, not organizations).
  useEffect(() => {
    if (coachOrgId || !profile?.id) return;
    if (tier !== 'coach_pro' && tier !== 'org_pro') return;
    reconcileCoachOrg(profile.id)
      .then(() => resolveCoachOwnerOrg(profile.id))
      .then((id) => { if (id) setCoachOrgId(id); })
      .catch(() => {});
  }, [profile?.id, tier, coachOrgId]);

  // Load coach setup banner dismissed state
  useEffect(() => {
    AsyncStorage.getItem('@duel_coach_setup_banner_dismissed').then((val) => {
      setSetupBannerDismissed(val === 'true');
    }).catch(() => setSetupBannerDismissed(false));
  }, []);

  const loadProgramsList = useCallback(async () => {
    if (!profile?.id) return;
    const list = await loadPrograms(profile.id);
    setPrograms(list);
  }, [profile?.id]);

  const loadMessagesList = useCallback(async () => {
    const msgs = await loadCoachMessages();
    setMessages(msgs);
  }, []);

  const loadTrainerProfile = useCallback(async () => {
    const tp = await getMyTrainerProfile();
    setTrainerProfile(tp);
  }, []);

  const loadTodaySessions = useCallback(async () => {
    if (!profile?.id) return;
    const sessions = await getTodaySessions(profile.id);
    setTodaySessionCount(sessions.length);
  }, [profile?.id]);

  const loadGymMembers = useCallback(async () => {
    if (!profile?.id) return;
    try {
      // V1: resolve coach's org_id from organization_memberships.
      // profile.gym_id is a gyms table FK and must NOT be used as org_id —
      // they are different UUID namespaces. See audit C5 / DUEL_CLUB_MODE_ARCHITECTURE.md.
      // V1 single-org selection: take the first active coach/admin org membership.
      // Multi-org switching UI is deferred to a later phase.
      const { data: coachMembership } = await supabase
        .from('organization_memberships')
        .select('org_id')
        .eq('user_id', profile.id)
        .in('role', ['coach', 'admin', 'owner'])
        .eq('status', 'active')
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (!coachMembership?.org_id) {
        // Coach has no active org membership — empty state, do not crash
        setGymMembers([]);
        return;
      }

      const { data: members } = await supabase
        .from('organization_memberships')
        .select('user_id')
        .eq('org_id', coachMembership.org_id)
        .eq('status', 'active');
      if (!members || members.length === 0) return;
      const memberIds = members.map((m: any) => m.user_id).filter((id: string) => id !== profile.id);
      if (memberIds.length === 0) return;
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, display_name, handle, avatar_url')
        .in('id', memberIds);
      setGymMembers((profiles ?? []) as any);
    } catch (err) {
      console.warn('[CoachDash] loadGymMembers error:', err);
    }
  }, [profile?.id]);

  const pickAndUploadImage = async (bucket: string, path: string): Promise<string | null> => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });
    if (result.canceled || !result.assets?.[0]) return null;
    const asset = result.assets[0];
    let contentType = asset.mimeType || 'image/jpeg';
    const response = await fetch(asset.uri);
    const fileBody = await response.blob();
    const { error } = await supabase.storage
      .from(bucket)
      .upload(path, fileBody, { upsert: true, contentType });
    if (error) throw error;
    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);
    return `${urlData.publicUrl}?t=${Date.now()}`;
  };

  const loadTagsData = useCallback(async () => {
    if (!profile?.id) return;
    const t = await loadTags(profile.id);
    setTags(t);
    const a = await loadAssignments(profile.id);
    setTagAssignments(a);
  }, [profile?.id]);

  useEffect(() => {
    loadProgramsList();
    loadMessagesList();
    loadTrainerProfile();
    loadTodaySessions();
    loadGymMembers();
    loadTagsData();
  }, [loadProgramsList, loadMessagesList, loadTrainerProfile, loadTodaySessions, loadGymMembers, loadTagsData]);

  const loadClients = useCallback(async () => {
    if (!profile?.id) return;
    try {
      const links = await listMyClientLinks();
      const activeLinks = links.filter((l: TrainerClientLink) => l.status === 'active');
      const clientIds = activeLinks.map((l: TrainerClientLink) => l.client_profile_id);

      if (clientIds.length === 0) {
        setClients([]);
        setStats({ activeClients: 0, workoutsThisWeek: 0, avgAdherence: 0 });
        return;
      }

      // Resolve profiles
      const profiles = await Promise.all(clientIds.map((id: string) => resolveProfileById(id)));
      const profileMap = new Map(profiles.filter(Boolean).map((p) => [p!.id, p!]));

      // Fetch strength scores + freshness + baseline from profiles table
      const { data: profileData } = await supabase
        .from('profiles')
        .select('id, strength_score, freshness_band, last_active, baseline_complete_pct')
        .in('id', clientIds);

      const profileDataMap = new Map((profileData || []).map((p: any) => [p.id, p]));

      // Workouts this week
      const d7 = new Date();
      d7.setDate(d7.getDate() - 7);
      const { count: weekWorkouts } = await supabase
        .from('workouts')
        .select('id', { count: 'exact', head: true })
        .in('user_id', clientIds)
        .gte('performed_at', d7.toISOString());

      // Build client cards
      const cards: ClientCardData[] = clientIds.map((id: string) => {
        const pub = profileMap.get(id);
        const pd = profileDataMap.get(id);
        return {
          id,
          name: pub?.display_name || pub?.username || 'Unknown',
          handle: pub?.username || '',
          avatar_url: null,
          strength_score: pd?.strength_score ?? 0,
          freshness_band: pd?.freshness_band ?? 'gray',
          last_active: pd?.last_active ?? '',
          baseline_pct: pd?.baseline_complete_pct ?? 0,
        };
      });

      // Avg adherence (simple: % of clients active in last 7 days)
      const activeRecently = cards.filter((cl) => {
        if (!cl.last_active) return false;
        return Date.now() - new Date(cl.last_active).getTime() < 7 * 86400000;
      }).length;

      setClients(cards);
      setStats({
        activeClients: cards.length,
        workoutsThisWeek: weekWorkouts ?? 0,
        avgAdherence: cards.length > 0 ? Math.round((activeRecently / cards.length) * 100) : 0,
      });

      // Detect recent PRs (last 7 days)
      try {
        const d7pr = new Date();
        d7pr.setDate(d7pr.getDate() - 7);
        const { data: prData } = await supabase
          .from('strength_events')
          .select('profile_id')
          .in('profile_id', clientIds)
          .eq('is_pr', true)
          .gte('created_at', d7pr.toISOString());
        if (prData) {
          setPrClientIds(new Set(prData.map((p: any) => p.profile_id)));
        }
      } catch { /* non-critical */ }

      // Load revenue snapshot
      const rev = await getRevenueSnapshot(cards.length);
      setRevenue(rev);
    } catch (err) {
      console.warn('Coach dashboard load error:', err);
      captureError(err, { context: 'coach-dashboard.loadClients' });
    }
  }, [profile?.id]);

  useEffect(() => {
    setLoading(true);
    loadClients().finally(() => setLoading(false));
  }, [loadClients]);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadClients();
    setRefreshing(false);
  };

  const filtered = clients
    .filter((cl) => {
      // Tag filter
      if (activeTagFilter) {
        const clientTags = tagAssignments[cl.id] ?? [];
        if (!clientTags.includes(activeTagFilter)) return false;
      }
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return cl.name.toLowerCase().includes(q) || cl.handle.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (sortBy === 'strength') return b.strength_score - a.strength_score;
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      // 'recent' — most recently active first
      const aTime = a.last_active ? new Date(a.last_active).getTime() : 0;
      const bTime = b.last_active ? new Date(b.last_active).getTime() : 0;
      return bTime - aTime;
    });

  // Guard: render loading spinner while access check is in progress, or bail if unauthorized.
  // Redirect is handled by the useEffect above; this just prevents flashing the dashboard.
  if (accessLoading || !hasAccess) {
    return (
      <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
        <ActivityIndicator style={{ flex: 1 }} color={c.accent} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.canGoBack() ? router.back() : router.replace('/')} style={styles.backBtn}>
          <ArrowLeft size={22} color={c.text} />
        </TouchableOpacity>
        <View style={{ flex: 1 }}>
          <Text style={[styles.headerTitle, { color: c.text }]}>Coach Dashboard</Text>
          {profile?.display_name ? (
            <Text style={{ color: c.textSecondary, fontSize: 12, fontFamily: TYPO.body.regular, marginTop: 1 }}>
              {profile.display_name}
            </Text>
          ) : null}
        </View>
        {/* Role pill */}
        <View style={[styles.rolePill, { backgroundColor: c.accent + '22', borderColor: c.accent + '44' }]}>
          <Text style={[styles.rolePillText, { color: c.accent }]}>COACH</Text>
        </View>
        {/* Export CSV — secondary action */}
        <TouchableOpacity
          onPress={async () => {
            if (!profile?.id) return;
            try {
              const rosterClients = clients.map((cl) => ({
                id: cl.id,
                handle: cl.handle || null,
                display_name: cl.name || null,
                lifting_score: cl.strength_score,
                last_workout_at: cl.last_active || null,
              }));
              await exportCoachRoster(profile.id, rosterClients);
            } catch (err: any) {
              Alert.alert('Export Failed', err.message || 'Could not export roster.');
            }
          }}
          style={[styles.addBtn, { opacity: clients.length === 0 ? 0.4 : 1 }]}
          disabled={clients.length === 0}
        >
          <Download size={18} color={c.textSecondary} />
        </TouchableOpacity>
        <TouchableOpacity onPress={() => setShowInvite(true)} style={styles.addBtn}>
          <Plus size={22} color={c.accent} />
        </TouchableOpacity>
      </View>

      {/* Stats Row */}
      <View style={[styles.statsRow, { borderColor: c.border }]}>
        <View style={styles.statItem}>
          <Text style={[styles.statValue, { color: c.text }]}>{stats.activeClients}</Text>
          <Text style={[styles.statLabel, { color: c.textSecondary }]}>Clients</Text>
        </View>
        <View style={[styles.statDivider, { backgroundColor: c.border }]} />
        <View style={styles.statItem}>
          <Text style={[styles.statValue, { color: c.text }]}>{stats.workoutsThisWeek}</Text>
          <Text style={[styles.statLabel, { color: c.textSecondary }]}>Workouts (7d)</Text>
        </View>
        <View style={[styles.statDivider, { backgroundColor: c.border }]} />
        <View style={styles.statItem}>
          <Text style={[styles.statValue, { color: c.text }]}>{stats.avgAdherence}%</Text>
          <Text style={[styles.statLabel, { color: c.textSecondary }]}>Adherence</Text>
        </View>
      </View>

      {/* Today's Sessions — always visible above tabs */}
      <TouchableOpacity
        style={[styles.todaySchedule, { backgroundColor: c.surface, borderColor: c.border }]}
        onPress={() => router.push('/coach-schedule' as any)}
        activeOpacity={0.7}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Calendar size={20} color={c.accent} />
          <Text style={[styles.manageSectionTitle, { color: c.text }]}>Today's Sessions</Text>
        </View>
        <Text style={{ color: todaySessionCount > 0 ? c.text : c.textSecondary, fontSize: 14, fontFamily: TYPO.body.regular }}>
          {todaySessionCount > 0
            ? `${todaySessionCount} session${todaySessionCount !== 1 ? 's' : ''} scheduled today`
            : 'No sessions scheduled today'}
        </Text>
        <Text style={{ color: c.accent, fontSize: 13, fontFamily: TYPO.body.semibold, marginTop: 8 }}>
          View full schedule →
        </Text>
      </TouchableOpacity>

      {/* Tab Switcher */}
      <View style={[styles.tabRow, { borderColor: c.border }]}>
        {(['clients', 'programs', 'messages', 'manage'] as const).map((t) => (
          <TouchableOpacity
            key={t}
            style={[styles.tab, activeTab === t && { borderBottomColor: c.accent, borderBottomWidth: 2 }]}
            onPress={() => setActiveTab(t)}
          >
            <Text style={[styles.tabText, { color: activeTab === t ? c.accent : c.textSecondary }]}>
              {t === 'clients' ? 'Clients' : t === 'programs' ? 'Programs' : t === 'messages' ? 'Messages' : 'Manage'}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {activeTab === 'manage' ? (
        <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
          {/* Coach Profile — Display or Create */}
          <View style={[styles.manageSection, { backgroundColor: c.surface, borderColor: c.border }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Settings size={18} color={c.accent} />
              <Text style={[styles.manageSectionTitle, { color: c.text }]}>Coach Profile</Text>
            </View>
            {trainerProfile && !editingProfile ? (
              /* ── Task 5: Profile preview ── */
              <View style={{ gap: 12 }}>
                {/* Avatar */}
                <View style={{ alignItems: 'center', gap: 8 }}>
                  <TouchableOpacity
                    onPress={async () => {
                      if (!profile?.id) return;
                      setUploadingAvatar(true);
                      try {
                        const url = await pickAndUploadImage('avatars', `${profile.id}/coach-avatar.jpg`);
                        if (url) {
                          await updateTrainerProfile({ avatar_url: url });
                          await loadTrainerProfile();
                        }
                      } catch (err: any) {
                        Alert.alert('Upload Failed', "Couldn't upload your photo. Please try again.");
                      } finally {
                        setUploadingAvatar(false);
                      }
                    }}
                    disabled={uploadingAvatar}
                  >
                    {trainerProfile.avatar_url ? (
                      <Image source={{ uri: trainerProfile.avatar_url }} style={styles.profileAvatar} />
                    ) : (
                      <View style={[styles.profileAvatar, { backgroundColor: c.surfaceVariant, alignItems: 'center', justifyContent: 'center' }]}>
                        <Camera size={28} color={c.textTertiary} />
                      </View>
                    )}
                    {uploadingAvatar && <ActivityIndicator style={{ position: 'absolute' }} color={c.accent} />}
                  </TouchableOpacity>
                  <Text style={{ fontSize: 11, color: c.textTertiary }}>Tap to change photo</Text>
                </View>

                {/* Bio */}
                {trainerProfile.bio ? (
                  <Text style={{ color: c.text, fontSize: 14, fontFamily: TYPO.body.regular }}>{trainerProfile.bio}</Text>
                ) : (
                  <Text style={{ color: c.textTertiary, fontSize: 13, fontStyle: 'italic' }}>No bio set</Text>
                )}

                {/* Certification badge */}
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Shield size={16} color={trainerProfile.verification_status === 'verified' ? '#22c55e' : c.textTertiary} />
                  <Text style={{ color: c.text, fontSize: 13, fontFamily: TYPO.body.semibold }}>
                    {trainerProfile.verification_status === 'verified' ? 'Verified Coach' : `Status: ${trainerProfile.verification_status}`}
                  </Text>
                  {(trainerProfile.cert_file_url || trainerProfile.cert_url) && (
                    <Text style={{ color: c.accent, fontSize: 11 }}>✓ Cert uploaded</Text>
                  )}
                </View>

                {/* Specializations */}
                {trainerProfile.specializations && trainerProfile.specializations.length > 0 && (
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                    {trainerProfile.specializations.map((s) => (
                      <View key={s} style={[styles.specTag, { backgroundColor: c.accent + '18' }]}>
                        <Text style={[styles.specTagText, { color: c.accent }]}>{s}</Text>
                      </View>
                    ))}
                  </View>
                )}

                {/* Edit button */}
                <TouchableOpacity
                  style={[styles.inviteBtn, { backgroundColor: c.surfaceVariant, alignSelf: 'flex-start' }]}
                  onPress={() => {
                    setEditBio(trainerProfile.bio || '');
                    setEditingProfile(true);
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <Edit2 size={14} color={c.text} />
                    <Text style={[styles.inviteBtnText, { color: c.text }]}>Edit Profile</Text>
                  </View>
                </TouchableOpacity>
              </View>
            ) : trainerProfile && editingProfile ? (
              /* ── Edit mode ── */
              <View style={{ gap: 10 }}>
                <Text style={{ color: c.textSecondary, fontSize: 12, fontFamily: TYPO.body.semibold }}>BIO</Text>
                <TextInput
                  style={[styles.manageInput, { backgroundColor: c.surfaceVariant, color: c.text, borderColor: c.border, minHeight: 80, textAlignVertical: 'top' }]}
                  placeholder="Tell clients about yourself..."
                  placeholderTextColor={c.textTertiary}
                  value={editBio}
                  onChangeText={(t) => setEditBio(t.slice(0, 200))}
                  multiline
                  maxLength={200}
                />
                <Text style={{ color: c.textTertiary, fontSize: 11, alignSelf: 'flex-end' }}>{editBio.length}/200</Text>

                {/* Upload cert */}
                <TouchableOpacity
                  style={[styles.inviteBtn, { backgroundColor: c.surfaceVariant }]}
                  onPress={async () => {
                    if (!profile?.id) return;
                    setUploadingCert(true);
                    try {
                      const url = await pickAndUploadImage('certifications', `${profile.id}/cert.jpg`);
                      if (url) {
                        await updateTrainerProfile({ cert_file_url: url });
                        await loadTrainerProfile();
                        Alert.alert('Uploaded', 'Certification uploaded successfully');
                      }
                    } catch (err: any) {
                      Alert.alert('Upload Failed', "Couldn't upload your certification. Please try again.");
                    } finally {
                      setUploadingCert(false);
                    }
                  }}
                  disabled={uploadingCert}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <Upload size={14} color={c.text} />
                    <Text style={[styles.inviteBtnText, { color: c.text }]}>
                      {uploadingCert ? 'Uploading...' : 'Upload Certification'}
                    </Text>
                  </View>
                </TouchableOpacity>

                <View style={{ flexDirection: 'row', gap: 10, marginTop: 4 }}>
                  <TouchableOpacity
                    style={[styles.inviteBtn, { backgroundColor: c.accent, flex: 1 }]}
                    onPress={async () => {
                      setActionLoading(true);
                      try {
                        await updateTrainerProfile({ bio: editBio.trim() || null } as any);
                        await loadTrainerProfile();
                        setEditingProfile(false);
                      } catch (err: any) {
                        Alert.alert('Oops', "Couldn't save your profile. Please try again.");
                      } finally {
                        setActionLoading(false);
                      }
                    }}
                    disabled={actionLoading}
                  >
                    <Text style={[styles.inviteBtnText, { color: c.accentForeground }]}>Save</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.inviteBtn, { backgroundColor: c.surfaceVariant, flex: 1 }]}
                    onPress={() => setEditingProfile(false)}
                  >
                    <Text style={[styles.inviteBtnText, { color: c.text }]}>Cancel</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : (
              /* ── Create profile form ── */
              <View style={{ gap: 8 }}>
                <Text style={{ color: c.textSecondary, fontSize: 13 }}>
                  Create your coach profile to accept client requests.
                </Text>
                <TextInput
                  style={[styles.manageInput, { backgroundColor: c.surfaceVariant, color: c.text, borderColor: c.border, minHeight: 80, textAlignVertical: 'top' }]}
                  placeholder="Bio — tell clients about yourself (optional)"
                  placeholderTextColor={c.textTertiary}
                  value={bio}
                  onChangeText={(t) => setBio(t.slice(0, 200))}
                  multiline
                  maxLength={200}
                />
                <Text style={{ color: c.textTertiary, fontSize: 11, alignSelf: 'flex-end' }}>{bio.length}/200</Text>

                {/* Upload cert instead of URL */}
                <TouchableOpacity
                  style={[styles.inviteBtn, { backgroundColor: c.surfaceVariant }]}
                  onPress={async () => {
                    if (!profile?.id) return;
                    setUploadingCert(true);
                    try {
                      const url = await pickAndUploadImage('certifications', `${profile.id}/cert.jpg`);
                      if (url) {
                        // Store temporarily — will be saved on create
                        (globalThis as any).__pendingCertUrl = url;
                        Alert.alert('Uploaded', 'Certification will be saved with your profile');
                      }
                    } catch (err: any) {
                      Alert.alert('Upload Failed', "Couldn't upload your certification. Please try again.");
                    } finally {
                      setUploadingCert(false);
                    }
                  }}
                  disabled={uploadingCert}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <Upload size={14} color={c.text} />
                    <Text style={[styles.inviteBtnText, { color: c.text }]}>
                      {uploadingCert ? 'Uploading...' : 'Upload Certification (optional)'}
                    </Text>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.inviteBtn, { backgroundColor: c.accent }]}
                  onPress={async () => {
                    setActionLoading(true);
                    try {
                      const certFileUrl = (globalThis as any).__pendingCertUrl || undefined;
                      await createTrainerProfile({
                        bio: bio.trim() || undefined,
                        cert_file_url: certFileUrl,
                      } as Partial<TrainerProfile>);
                      delete (globalThis as any).__pendingCertUrl;
                      await loadTrainerProfile();
                      setBio('');
                    } catch (err: any) {
                      Alert.alert('Oops', "Couldn't create your profile. Please try again.");
                    } finally {
                      setActionLoading(false);
                    }
                  }}
                  disabled={actionLoading}>
                  <Text style={[styles.inviteBtnText, { color: c.accentForeground }]}>Create Coach Profile</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* Add Client by Username */}
          <View style={[styles.manageSection, { backgroundColor: c.surface, borderColor: c.border }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <UserPlus size={18} color={c.accent} />
              <Text style={[styles.manageSectionTitle, { color: c.text }]}>Add Client by Username</Text>
            </View>
            <TextInput
              style={[styles.manageInput, { backgroundColor: c.surfaceVariant, color: c.text, borderColor: c.border }]}
              placeholder="Client username"
              placeholderTextColor={c.textTertiary}
              value={clientUsername}
              onChangeText={setClientUsername}
              autoCapitalize="none"
            />
            <TouchableOpacity
              style={[styles.inviteBtn, { backgroundColor: c.accent, opacity: clientUsername.trim() ? 1 : 0.5 }]}
              onPress={async () => {
                if (!clientUsername.trim()) return;
                setActionLoading(true);
                try {
                  await requestTrainerClientLinkByUsername(clientUsername.trim());
                  Alert.alert('Sent', 'Client request sent');
                  setClientUsername('');
                  loadClients();
                } catch (err: any) {
                  Alert.alert('Oops', "Couldn't send the request. Please try again.");
                } finally {
                  setActionLoading(false);
                }
              }}
              disabled={!clientUsername.trim() || actionLoading}>
              <Text style={[styles.inviteBtnText, { color: c.accentForeground }]}>Send Request</Text>
            </TouchableOpacity>
          </View>

          {/* Task 3: Org Members (V1: shows members from coach's first active org) */}
          {gymMembers.length > 0 && (
            <View style={[styles.manageSection, { backgroundColor: c.surface, borderColor: c.border }]}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Users size={18} color={c.accent} />
                <Text style={[styles.manageSectionTitle, { color: c.text }]}>Gym Members</Text>
              </View>
              <TextInput
                style={[styles.manageInput, { backgroundColor: c.surfaceVariant, color: c.text, borderColor: c.border }]}
                placeholder="Search members..."
                placeholderTextColor={c.textTertiary}
                value={gymMemberSearch}
                onChangeText={setGymMemberSearch}
                autoCapitalize="none"
              />
              {gymMembers
                .filter((m) => {
                  if (!gymMemberSearch.trim()) return true;
                  const q = gymMemberSearch.toLowerCase();
                  return (m.display_name || '').toLowerCase().includes(q) || (m.handle || '').toLowerCase().includes(q);
                })
                .slice(0, 20)
                .map((member) => (
                  <View key={member.id} style={[styles.gymMemberRow, { borderColor: c.border }]}>
                    {member.avatar_url ? (
                      <Image source={{ uri: member.avatar_url }} style={styles.gymMemberAvatar} />
                    ) : (
                      <View style={[styles.gymMemberAvatar, { backgroundColor: c.surfaceVariant }]} />
                    )}
                    <View style={{ flex: 1 }}>
                      <Text style={{ color: c.text, fontSize: 14, fontFamily: TYPO.body.semibold }}>
                        {member.display_name || member.handle || 'Member'}
                      </Text>
                      {member.handle && (
                        <Text style={{ color: c.textSecondary, fontSize: 12 }}>@{member.handle}</Text>
                      )}
                    </View>
                    <TouchableOpacity
                      style={[styles.inviteSmallBtn, { backgroundColor: c.accent }]}
                      onPress={async () => {
                        setActionLoading(true);
                        try {
                          if (member.handle) {
                            await requestTrainerClientLinkByUsername(member.handle, coachOrgId ?? undefined);
                          } else {
                            // Direct insert — coachOrgId must be set (org_id NOT NULL constraint)
                            const { data: { user } } = await supabase.auth.getUser();
                            if (user && coachOrgId) {
                              await supabase.from('coach_athlete_links').insert({
                                org_id: coachOrgId,
                                coach_user_id: user.id,
                                athlete_user_id: member.id,
                                status: 'pending',
                              });
                            } else if (!coachOrgId) {
                              Alert.alert('Not Ready', 'Coach org not yet provisioned. Please try again in a moment.');
                              return;
                            }
                          }
                          Alert.alert('Sent', `Invite sent to ${member.display_name || member.handle}`);
                        } catch (err: any) {
                          Alert.alert('Oops', "Couldn't send the invite. Please try again.");
                        } finally {
                          setActionLoading(false);
                        }
                      }}
                      disabled={actionLoading}
                    >
                      <Text style={{ color: c.accentForeground, fontSize: 12, fontFamily: TYPO.body.bold }}>Invite</Text>
                    </TouchableOpacity>
                  </View>
                ))}
            </View>
          )}
        </ScrollView>
      ) : activeTab === 'messages' ? (
        <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
          <TouchableOpacity
            style={[styles.inviteBtn, { backgroundColor: c.accent, alignSelf: 'stretch', marginBottom: 16 }]}
            onPress={() => setShowGroupMessage(true)}>
            <Text style={[styles.inviteBtnText, { color: c.accentForeground }]}>+ New Group Message</Text>
          </TouchableOpacity>
          {messages.length === 0 ? (
            <View style={styles.center}>
              <MessageSquare size={48} color={c.textTertiary} />
              <Text style={[styles.emptyTitle, { color: c.textSecondary }]}>No messages sent yet</Text>
            </View>
          ) : (
            messages.map((msg) => (
              <View key={msg.id} style={[styles.programCard, { backgroundColor: c.surface, borderColor: c.border }]}>
                <View style={{ flex: 1, gap: 4 }}>
                  <Text style={[styles.programName, { color: c.text }]} numberOfLines={2}>{msg.text}</Text>
                  <Text style={[styles.programMeta, { color: c.textSecondary }]}>
                    To: {msg.recipientNames.join(', ')}
                  </Text>
                  <Text style={[styles.programMeta, { color: c.textTertiary }]}>
                    {new Date(msg.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </Text>
                </View>
              </View>
            ))
          )}
        </ScrollView>
      ) : activeTab === 'programs' ? (
        <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
          <TouchableOpacity
            style={[styles.inviteBtn, { backgroundColor: c.accent, alignSelf: 'stretch', marginBottom: 16 }]}
            onPress={() => { setEditingProgram(undefined); setShowProgramBuilder(true); }}>
            <Text style={[styles.inviteBtnText, { color: c.accentForeground }]}>+ Create Program</Text>
          </TouchableOpacity>
          {programs.length === 0 ? (
            <View style={styles.center}>
              <ClipboardList size={48} color={c.textTertiary} />
              <Text style={[styles.emptyTitle, { color: c.textSecondary }]}>No programs yet</Text>
            </View>
          ) : (
            programs.map((prog) => (
              <View key={prog.id} style={[styles.programCard, { backgroundColor: c.surface, borderColor: c.border }]}>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.programName, { color: c.text }]}>{prog.name}</Text>
                  <Text style={[styles.programMeta, { color: c.textSecondary }]}>
                    {prog.days.length} day{prog.days.length !== 1 ? 's' : ''} · {prog.assignedTo?.length || 0} assigned
                  </Text>
                </View>
                <View style={{ flexDirection: 'row', gap: 12, alignItems: 'center' }}>
                  <TouchableOpacity onPress={() => { setEditingProgram(prog); setShowProgramBuilder(true); }}>
                    <Text style={{ color: c.accent, fontWeight: '600', fontSize: 13 }}>Edit</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={async () => {
                    await deleteProgram(prog.id);
                    loadProgramsList();
                  }}>
                    <Trash2 size={16} color={c.textTertiary} />
                  </TouchableOpacity>
                </View>
              </View>
            ))
          )}
        </ScrollView>
      ) : (
      <>
      {/* Revenue Section — tap for full dashboard */}
      {revenue && (
        <TouchableOpacity
          style={[styles.revenueRow, { borderColor: c.border }]}
          onPress={() => setShowRevenueDashboard(true)}
          activeOpacity={0.7}
        >
          <View style={styles.statItem}>
            <Text style={[styles.statValue, { color: c.success }]}>${revenue.monthlyRevenue.toLocaleString()}</Text>
            <Text style={[styles.statLabel, { color: c.textSecondary }]}>Monthly Est.</Text>
          </View>
          <View style={[styles.statDivider, { backgroundColor: c.border }]} />
          <View style={styles.statItem}>
            <Text style={[styles.statValue, { color: c.text }]}>${revenue.monthlyRate}</Text>
            <Text style={[styles.statLabel, { color: c.textSecondary }]}>Per Client</Text>
          </View>
          <View style={[styles.statDivider, { backgroundColor: c.border }]} />
          <View style={styles.statItem}>
            <Text style={[styles.statValue, { color: revenue.retentionRate >= 80 ? c.success : c.warning }]}>
              {revenue.retentionRate}%
            </Text>
            <Text style={[styles.statLabel, { color: c.textSecondary }]}>Retention</Text>
          </View>
        </TouchableOpacity>
      )}

      {/* Action buttons */}
      <View style={styles.actionRow}>
        <TouchableOpacity
          style={[styles.actionBtn, { backgroundColor: c.surface, borderColor: c.border }]}
          onPress={() => setShowComparison(true)}>
          <BarChart2 size={16} color={c.accent} />
          <Text style={[styles.actionBtnText, { color: c.accent }]}>Compare</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.actionBtn, { backgroundColor: c.surface, borderColor: c.border }]}
          onPress={() => setShowGroupMessage(true)}>
          <MessageSquare size={16} color={c.accent} />
          <Text style={[styles.actionBtnText, { color: c.accent }]}>Message</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.actionBtn, { backgroundColor: c.surface, borderColor: c.border }]}
          onPress={() => setShowTagManager(true)}>
          <Tag size={16} color={c.accent} />
          <Text style={[styles.actionBtnText, { color: c.accent }]}>Tags</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.actionBtn, { backgroundColor: c.surface, borderColor: c.border }]}
          onPress={() => router.push('/coach-programs' as any)}>
          <ClipboardList size={16} color={c.accent} />
          <Text style={[styles.actionBtnText, { color: c.accent }]}>Programs</Text>
        </TouchableOpacity>
      </View>

      {/* Coach Setup Banner — first-run prompt when no profile and no clients */}
      {!setupBannerDismissed && clients.length === 0 && trainerProfile === null && (
        <View style={[styles.setupBanner, { backgroundColor: c.surface, borderColor: c.accent + '55' }]}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Text style={[styles.setupBannerTitle, { color: c.text }]}>Set Up Your Coach Profile</Text>
            <TouchableOpacity
              onPress={async () => {
                setSetupBannerDismissed(true);
                await AsyncStorage.setItem('@duel_coach_setup_banner_dismissed', 'true');
              }}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Text style={{ color: c.textTertiary, fontSize: 18, lineHeight: 20 }}>×</Text>
            </TouchableOpacity>
          </View>
          <Text style={[styles.setupBannerBody, { color: c.textSecondary }]}>
            Add your bio, certifications, and specialties so clients know who you are.
          </Text>
          <TouchableOpacity
            style={[styles.setupBannerBtn, { backgroundColor: c.accent }]}
            onPress={() => setActiveTab('manage')}
          >
            <Text style={[styles.setupBannerBtnText, { color: c.accentForeground }]}>Get Started →</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* DEV: Seed Test Clients */}
      {isDev && clients.length === 0 && (
        <TouchableOpacity
          style={[styles.actionBtn, { backgroundColor: '#7c3aed', borderColor: '#7c3aed', marginHorizontal: 16, marginTop: 8 }]}
          onPress={() => {
            const now = new Date();
            const mockClients: ClientCardData[] = [
              { id: 'mock-1', name: 'Alex Rivera', handle: 'alexr', avatar_url: null, strength_score: 872, freshness_band: 'green', last_active: new Date(now.getTime() - 1 * 86400000).toISOString(), baseline_pct: 85 },
              { id: 'mock-2', name: 'Jordan Kim', handle: 'jordank', avatar_url: null, strength_score: 745, freshness_band: 'yellow', last_active: new Date(now.getTime() - 5 * 86400000).toISOString(), baseline_pct: 100 },
              { id: 'mock-3', name: 'Sam Okafor', handle: 'samokafor', avatar_url: null, strength_score: 680, freshness_band: 'red', last_active: new Date(now.getTime() - 18 * 86400000).toISOString(), baseline_pct: 60 },
              { id: 'mock-4', name: 'Casey Chen', handle: 'caseyc', avatar_url: null, strength_score: 815, freshness_band: 'green', last_active: new Date(now.getTime() - 2 * 86400000).toISOString(), baseline_pct: 100 },
              { id: 'mock-5', name: 'Morgan Wells', handle: 'morganw', avatar_url: null, strength_score: 610, freshness_band: 'gray', last_active: new Date(now.getTime() - 21 * 86400000).toISOString(), baseline_pct: 40 },
            ];
            setClients(mockClients);
            setStats({ activeClients: 5, workoutsThisWeek: 12, avgAdherence: 60 });
            if (profile?.id) {
              seedMockSessions(profile.id).then(() => loadTodaySessions());
            }
          }}
        >
          <Text style={[styles.actionBtnText, { color: '#fff' }]}>🧪 Seed Test Clients</Text>
        </TouchableOpacity>
      )}

      {/* Tag Filter */}
      <TagFilterBar tags={tags} activeTagId={activeTagFilter} onSelect={setActiveTagFilter} />

      {/* Sort Controls */}
      <View style={styles.sortRow}>
        {(['recent', 'strength', 'name'] as const).map((s) => (
          <TouchableOpacity
            key={s}
            style={[styles.sortBtn, sortBy === s && { backgroundColor: c.accent + '22' }]}
            onPress={() => setSortBy(s)}
          >
            <Text style={[styles.sortBtnText, { color: sortBy === s ? c.accent : c.textTertiary }]}>
              {s === 'recent' ? 'Recent' : s === 'strength' ? 'Strength' : 'Name'}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Search */}
      <View style={[styles.searchRow, { borderColor: c.border, backgroundColor: c.surface }]}>
        <Search size={16} color={c.textTertiary} />
        <TextInput
          style={[styles.searchInput, { color: c.text }]}
          placeholder="Search clients..."
          placeholderTextColor={c.textTertiary}
          value={search}
          onChangeText={setSearch}
        />
      </View>

      {/* Client List */}
      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={c.accent} />
        </View>
      ) : clients.length === 0 ? (
        /* ── Zero-client first-run onboarding ── */
        <ScrollView contentContainerStyle={[styles.listContent, { alignItems: 'center', justifyContent: 'center', flexGrow: 1, paddingHorizontal: 32 }]}>
          <Users size={56} color={c.textTertiary} style={{ marginBottom: 16 }} />
          <Text style={[styles.emptyTitle, { color: c.text, fontSize: 22, textAlign: 'center', marginBottom: 8 }]}>
            Your Coaching Workspace
          </Text>
          <Text style={{ color: c.textSecondary, fontSize: 14, textAlign: 'center', lineHeight: 21, marginBottom: 28 }}>
            Invite clients to connect them to your workspace. Once they join, you can assign programs and track their progress.
          </Text>
          {/* Primary CTA */}
          <TouchableOpacity
            style={[styles.inviteBtn, { backgroundColor: c.accent, alignSelf: 'stretch', marginBottom: 12, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 }]}
            onPress={() => setShowInvite(true)}
          >
            <UserPlus size={18} color={c.accentForeground} />
            <Text style={[styles.inviteBtnText, { color: c.accentForeground }]}>Invite Your First Client</Text>
          </TouchableOpacity>
          {/* Secondary CTA */}
          <TouchableOpacity
            style={[styles.inviteBtn, { backgroundColor: c.surface, borderWidth: 1, borderColor: c.border, alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 }]}
            onPress={() => setActiveTab('programs')}
          >
            <ClipboardList size={18} color={c.text} />
            <Text style={[styles.inviteBtnText, { color: c.text }]}>Create a Program</Text>
          </TouchableOpacity>
          {/* How it works microcopy */}
          <View style={{ alignSelf: 'stretch', marginTop: 32, gap: 10 }}>
            <Text style={{ color: c.textTertiary, fontSize: 11, fontFamily: TYPO.body.semibold, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
              How it works
            </Text>
            {[
              '1. Invite a client by username or email',
              '2. Create a training program',
              '3. Assign the program and track progress',
            ].map((step) => (
              <Text key={step} style={{ color: c.textSecondary, fontSize: 13, fontFamily: TYPO.body.regular, lineHeight: 19 }}>
                {step}
              </Text>
            ))}
          </View>
        </ScrollView>
      ) : filtered.length === 0 ? (
        <View style={styles.center}>
          <Text style={[styles.emptyTitle, { color: c.textSecondary }]}>No matching clients</Text>
          <Text style={{ color: c.textTertiary, fontSize: 13 }}>Try adjusting your search terms.</Text>
        </View>
      ) : (
        <ScrollView
          style={styles.list}
          contentContainerStyle={styles.listContent}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={c.accent} />}
        >
          <ProgressAlerts
            clients={clients}
            prClientIds={prClientIds}
            onPressClient={(id) => {
              const cl = clients.find((x) => x.id === id);
              if (cl) {
                router.push({ pathname: '/coach-client-detail', params: { clientId: cl.id, clientName: cl.name, clientHandle: cl.handle, strengthScore: String(cl.strength_score), freshnessBand: cl.freshness_band, baselinePct: String(cl.baseline_pct) } } as any);
              }
            }}
          />
          {filtered.map((cl) => (
            <ClientCard
              key={cl.id}
              client={cl}
              onPress={() => router.push({ pathname: '/coach-client-detail', params: { clientId: cl.id, clientName: cl.name, clientHandle: cl.handle, strengthScore: String(cl.strength_score), freshnessBand: cl.freshness_band, baselinePct: String(cl.baseline_pct) } } as any)}
            />
          ))}
          {/* View All / Roster link if more than 3 clients */}
          {clients.length > 3 && (
            <TouchableOpacity
              style={[styles.viewAllBtn, { borderColor: c.border }]}
              onPress={() => router.push({ pathname: '/coach-client-roster', params: { orgId: coachOrgId ?? '' } } as any)}
            >
              <Text style={[styles.viewAllText, { color: c.accent }]}>View All Clients ({clients.length}) →</Text>
            </TouchableOpacity>
          )}
          {/* Progression nudge: clients exist but no programs assigned */}
          {!dismissedNudge && clients.length > 0 && programs.length === 0 && (
            <View style={[styles.nudgeBanner, { backgroundColor: c.surface, borderColor: c.border }]}>
              <Text style={{ color: c.textSecondary, fontSize: 13, fontFamily: TYPO.body.regular, flex: 1 }}>
                💡 Tip: Assign a program to your clients to get started
              </Text>
              <TouchableOpacity onPress={() => setDismissedNudge(true)} style={{ paddingLeft: 12 }}>
                <Text style={{ color: c.textTertiary, fontSize: 13 }}>✕</Text>
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>
      )}

      </>
      )}

      {/* Invite Modal */}
      {showInvite && <ClientInvite onClose={() => setShowInvite(false)} />}

      {/* Program Builder Modal */}
      {showProgramBuilder && (
        <ProgramBuilder
          existingProgram={editingProgram}
          onClose={() => setShowProgramBuilder(false)}
          onSaved={() => loadProgramsList()}
        />
      )}

      {/* Group Message Modal */}
      <GroupMessage
        visible={showGroupMessage}
        clients={clients.map((cl) => ({ id: cl.id, name: cl.name }))}
        onClose={() => setShowGroupMessage(false)}
        onSent={() => loadMessagesList()}
      />

      {/* Client Comparison Modal */}
      <ClientComparison
        visible={showComparison}
        clients={clients.map((cl) => ({ id: cl.id, name: cl.name, strength_score: cl.strength_score }))}
        onClose={() => setShowComparison(false)}
      />

      {/* Revenue Dashboard Modal */}
      {profile?.id && (
        <RevenueDashboard
          visible={showRevenueDashboard}
          coachId={profile.id}
          clients={clients.map((cl) => ({ id: cl.id, name: cl.name }))}
          onClose={() => setShowRevenueDashboard(false)}
        />
      )}

      {/* Tag Manager Modal */}
      {profile?.id && (
        <TagManager
          visible={showTagManager}
          coachId={profile.id}
          tags={tags}
          onUpdate={(t) => setTags(t)}
          onClose={() => setShowTagManager(false)}
        />
      )}

      {/* Assign Tags Modal */}
      {profile?.id && showAssignTags && (
        <AssignClientTags
          visible={!!showAssignTags}
          coachId={profile.id}
          clientId={showAssignTags.clientId}
          clientName={showAssignTags.clientName}
          tags={tags}
          assignments={tagAssignments}
          onUpdate={(a) => setTagAssignments(a)}
          onClose={() => setShowAssignTags(null)}
        />
      )}
      <TutorialOverlay screenKey="coach_upgrade" />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
  },
  backBtn: { padding: 4 },
  headerTitle: { fontSize: 20, fontFamily: TYPO.heading.bold },
  addBtn: { padding: 4 },
  rolePill: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 2,
    borderWidth: 1,
    marginRight: 4,
  },
  rolePillText: {
    fontSize: 11,
    fontFamily: TYPO.body.bold,
    letterSpacing: 1,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  statItem: { flex: 1, alignItems: 'center', gap: 2 },
  statValue: { fontSize: 22, fontFamily: TYPO.heading.bold },
  statLabel: { fontSize: 11, fontFamily: TYPO.body.semibold },
  statDivider: { width: 1, height: 32 },
  sortRow: {
    flexDirection: 'row',
    marginHorizontal: 16,
    marginTop: 8,
    gap: 8,
  },
  sortBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 2,
  },
  sortBtnText: {
    fontSize: 12,
    fontWeight: '700',
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    marginTop: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 10,
    gap: 8,
  },
  searchInput: { flex: 1, height: 40, fontSize: 14, fontFamily: TYPO.body.regular },
  list: { flex: 1 },
  listContent: { paddingHorizontal: 16, paddingBottom: 32 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12 },
  emptyTitle: { fontSize: 16, fontFamily: TYPO.body.semibold },
  inviteBtn: {
    borderRadius: 2,
    paddingHorizontal: 20,
    paddingVertical: 10,
    marginTop: 4,
  },
  inviteBtnText: { fontFamily: TYPO.body.bold, fontSize: 14 },
  tabRow: {
    flexDirection: 'row',
    marginHorizontal: 16,
    borderBottomWidth: 1,
    marginTop: 8,
  },
  tab: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 10,
  },
  tabText: {
    fontSize: 14,
    fontFamily: TYPO.heading.semibold,
  },
  programCard: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 2,
    padding: 14,
    marginBottom: 10,
  },
  programName: {
    fontSize: 15,
    fontFamily: TYPO.body.bold,
  },
  programMeta: {
    fontSize: 12,
    fontFamily: TYPO.body.regular,
    marginTop: 2,
  },
  revenueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  actionRow: {
    flexDirection: 'row',
    gap: 10,
    marginHorizontal: 16,
    marginTop: 10,
  },
  actionBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderWidth: 1,
    borderRadius: 2,
    paddingVertical: 10,
  },
  actionBtnText: {
    fontSize: 13,
    fontFamily: TYPO.body.bold,
  },
  todaySchedule: {
    marginHorizontal: 16,
    marginTop: 12,
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
  },
  manageSection: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    marginBottom: 16,
  },
  manageSectionTitle: {
    fontSize: 16,
    fontFamily: TYPO.heading.bold,
  },
  manageInput: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 12,
    fontSize: 14,
    fontFamily: TYPO.body.regular,
    marginBottom: 8,
  },
  profileAvatar: {
    width: 80,
    height: 80,
    borderRadius: 4,
    overflow: 'hidden',
  },
  specTag: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 2,
  },
  specTagText: {
    fontSize: 12,
    fontFamily: TYPO.body.semibold,
  },
  gymMemberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  gymMemberAvatar: {
    width: 36,
    height: 36,
    borderRadius: 2,
  },
  inviteSmallBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 2,
  },
  viewAllBtn: {
    borderTopWidth: 1,
    paddingVertical: 14,
    alignItems: 'center',
    marginTop: 8,
  },
  viewAllText: {
    fontSize: 14,
    fontFamily: TYPO.body.semibold,
  },
  nudgeBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 2,
    padding: 12,
    marginTop: 12,
  },
  setupBanner: {
    borderWidth: 1.5,
    borderRadius: 4,
    padding: 16,
    marginHorizontal: 16,
    marginTop: 12,
    gap: 8,
  },
  setupBannerTitle: {
    fontSize: 15,
    fontFamily: TYPO.heading.bold,
    flex: 1,
  },
  setupBannerBody: {
    fontSize: 13,
    fontFamily: TYPO.body.regular,
    lineHeight: 19,
  },
  setupBannerBtn: {
    alignSelf: 'flex-start',
    paddingHorizontal: 16,
    paddingVertical: 9,
    borderRadius: 4,
    marginTop: 4,
  },
  setupBannerBtnText: {
    fontSize: 13,
    fontFamily: TYPO.body.semibold,
  },
});
